import { Workbook } from 'exceljs';

// ─── Partner List (obtUploadExcelFile) ────────────────────────────────────────

// Expected columns by index (0-based)
// Backend parser: row[0]=sysid, row[1]=partnerno, row[2]=companycode, row[3]=category
const REQUIRED_COLUMN_COUNT = 4;
const EXPECTED_HEADERS = ['System', 'Partner No', 'Company Code', 'Category'];

// Header row values the backend skips
const HEADER_PARTNER_NO_VALUES = ['Partnerno', 'PartnerNo', 'Partner No', 'partnerno'];

// ─── Partner Contacts (readPartnerContactFileFromBase64) ──────────────────────

// All 11 columns that must be present as headers (case-insensitive match)
const CONTACT_REQUIRED_HEADERS = [
    'system', 'partnerno', 'company', 'category',
    'role', 'email', 'firstname', 'lastname',
    'department', 'phone', 'mobile',
];

// Of those, only these 4 must be non-empty in every data row
const CONTACT_MANDATORY_ROW_FIELDS = ['system', 'partnerno', 'company', 'category'];

export interface PartnerListValidationResult {
    valid: boolean;
    errors: string[];
}

function isHeaderRow(cells: string[]): boolean {
    return HEADER_PARTNER_NO_VALUES.includes(cells[1] ?? '') || (cells[0] ?? '').toLowerCase() === 'system';
}

/**
 * Validates a partner list Excel file in the browser before sending to the backend.
 *
 * Rules:
 * 1. Must contain at least one worksheet.
 * 2. After skipping a header row, must have at least one data row.
 * 3. Every data row must have non-empty values in columns 0–3
 *    (System, Partner No, Company Code, Category).
 */
export async function validatePartnerListFile(file: File): Promise<PartnerListValidationResult> {
    let arrayBuffer: ArrayBuffer;
    try {
        arrayBuffer = await file.arrayBuffer();
    } catch {
        return { valid: false, errors: ['Could not read the file.'] };
    }

    const workbook = new Workbook();
    try {
        await workbook.xlsx.load(arrayBuffer);
    } catch {
        return {
            valid: false,
            errors: ['File could not be parsed as an Excel workbook. Please ensure it is a valid .xlsx / .xls / .xlsm file.'],
        };
    }

    const worksheet = workbook.worksheets[0];
    if (!worksheet) {
        return { valid: false, errors: ['The file contains no worksheets.'] };
    }

    // Collect all non-empty rows as string arrays
    const allRows: { rowNumber: number; cells: string[] }[] = [];
    worksheet.eachRow((row, rowNumber) => {
        const cells: string[] = [];
        for (let col = 1; col <= Math.max(row.cellCount, REQUIRED_COLUMN_COUNT); col++) {
            cells.push(row.getCell(col).text?.trim() ?? '');
        }
        if (cells.some((c) => c !== '')) {
            allRows.push({ rowNumber, cells });
        }
    });

    if (allRows.length === 0) {
        return { valid: false, errors: ['The file is empty.'] };
    }

    const dataRows = allRows.filter(({ cells }) => !isHeaderRow(cells));

    if (dataRows.length === 0) {
        return {
            valid: false,
            errors: [`The file contains no data rows. Expected at least one row with: ${EXPECTED_HEADERS.join(', ')}.`],
        };
    }

    const incompleteRows: number[] = [];
    dataRows.forEach(({ rowNumber, cells }) => {
        const hasMissing = EXPECTED_HEADERS.some((_, idx) => !cells[idx]);
        if (hasMissing) {
            incompleteRows.push(rowNumber);
        }
    });

    if (incompleteRows.length > 0) {
        const sample = incompleteRows.slice(0, 5).join(', ');
        const suffix = incompleteRows.length > 5 ? ` and ${incompleteRows.length - 5} more` : '';
        return {
            valid: false,
            errors: [
                `${incompleteRows.length} row(s) are missing required values (${EXPECTED_HEADERS.join(', ')}). Row(s): ${sample}${suffix}.`,
            ],
        };
    }

    return { valid: true, errors: [] };
}

/**
 * Validates a partner contacts Excel file in the browser before sending to the backend.
 *
 * Rules:
 * 1. Must contain at least one worksheet.
 * 2. First non-empty row must be a header row containing all 11 required columns
 *    (case-insensitive): System, PartnerNo, Company, Category, Role, Email,
 *    Firstname, Lastname, Department, Phone, Mobile.
 * 3. After the header, must have at least one data row.
 * 4. Every data row must have non-empty values in: System, PartnerNo, Company, Category.
 */
export async function validatePartnerContactsFile(file: File): Promise<PartnerListValidationResult> {
    let arrayBuffer: ArrayBuffer;
    try {
        arrayBuffer = await file.arrayBuffer();
    } catch {
        return { valid: false, errors: ['Could not read the file.'] };
    }

    const workbook = new Workbook();
    try {
        await workbook.xlsx.load(arrayBuffer);
    } catch {
        return {
            valid: false,
            errors: ['File could not be parsed as an Excel workbook. Please ensure it is a valid .xlsx / .xls / .xlsm file.'],
        };
    }

    const worksheet = workbook.worksheets[0];
    if (!worksheet) {
        return { valid: false, errors: ['The file contains no worksheets.'] };
    }

    // Collect all non-empty rows as lower-cased string arrays
    const allRows: { rowNumber: number; cells: string[] }[] = [];
    worksheet.eachRow((row, rowNumber) => {
        const cells: string[] = [];
        for (let col = 1; col <= row.cellCount; col++) {
            cells.push(row.getCell(col).text?.trim() ?? '');
        }
        if (cells.some((c) => c !== '')) {
            allRows.push({ rowNumber, cells });
        }
    });

    if (allRows.length === 0) {
        return { valid: false, errors: ['The file is empty.'] };
    }

    // First row is the header
    const headerCells = allRows[0].cells.map((c) => c.toLowerCase().replace(/\s+/g, ''));
    const missingHeaders = CONTACT_REQUIRED_HEADERS.filter((h) => !headerCells.includes(h));
    if (missingHeaders.length > 0) {
        return {
            valid: false,
            errors: [
                `The file is missing required column(s): ${missingHeaders.join(', ')}. ` +
                `All 11 columns must be present: ${CONTACT_REQUIRED_HEADERS.join(', ')}.`,
            ],
        };
    }

    // Build index map: normalised header name → column index
    const headerIndexMap: Record<string, number> = {};
    headerCells.forEach((h, idx) => { headerIndexMap[h] = idx; });

    const dataRows = allRows.slice(1);
    if (dataRows.length === 0) {
        return { valid: false, errors: ['The file contains no data rows after the header.'] };
    }

    const incompleteRows: number[] = [];
    dataRows.forEach(({ rowNumber, cells }) => {
        const missing = CONTACT_MANDATORY_ROW_FIELDS.some((field) => {
            const idx = headerIndexMap[field];
            return idx === undefined || !cells[idx];
        });
        if (missing) incompleteRows.push(rowNumber);
    });

    if (incompleteRows.length > 0) {
        const sample = incompleteRows.slice(0, 5).join(', ');
        const suffix = incompleteRows.length > 5 ? ` and ${incompleteRows.length - 5} more` : '';
        return {
            valid: false,
            errors: [
                `${incompleteRows.length} row(s) are missing required values ` +
                `(System, PartnerNo, Company, Category). Row(s): ${sample}${suffix}.`,
            ],
        };
    }

    return { valid: true, errors: [] };
}
